import { CloseIcon } from 'components/common/Icons';
import { FilterSelects } from 'components/core';
import { TFilterOptions } from 'utils/context/FilterContext/types';

import {
  Box,
  Flex,
  IconButton,
  Select,
  SelectProps,
} from '@project44/manifest-ui-deprecated';
import { useTranslation } from '@project44/translations-utils';

interface Props {
  handleSelect: (value: string) => void;
  icons: { [key: string]: () => JSX.Element };
  onClear: (value: string) => void;
  options: Array<{
    config: TFilterOptions;
    startVariant?: string;
    value: string;
  }>;
  placeholder?: string;
  value: string;
}

const styles = {
  root: {
    border: '1px solid',
    borderColor: 'neutral.200',
    borderRadius: '4px',
    position: 'relative',
    marginTop: '20px',
  },
  flex: {
    border: '1px solid',
    borderRadius: '4px',
    left: '1.25rem',
    position: 'absolute',
    top: '-20px',
    ':focus-within': {
      borderColor: 'primary.500',
      backgroundColor: 'fill.cerulean.50',
    },
  },
  select: {
    width: '225px',
    '& .manifestui-select__control': {
      border: 'none',
      fontSize: '16px',
      fontWeight: 'bold',
      ':focus-within': {
        background: 'gradient.focus',
        backgroundClip: 'text',
        textFillColor: 'transparent',
      },
      '&:after': {
        boxShadow: 'none !important',
        height: '44px',
        outline: 'none !important',
        width: '266px',
      },
    },
  },
};

function FilterSelectsGroup({
  handleSelect,
  icons = {},
  onClear,
  options,
  placeholder,
  value,
}: Props) {
  const { t } = useTranslation();
  const ListIcon = icons[value] || icons.default;
  const sel = options.find((option) => option.value === value);

  const handleChange = ({ value }: { value: string }) => {
    handleSelect(value);
  };

  return (
    <Box sx={styles.root}>
      <Flex
        sx={{
          ...styles.flex,
          backgroundColor: value ? 'neutral.50' : 'fill.cerulean.50',
          borderColor: value ? 'neutral.500' : 'primary.500',
        }}
      >
        <Box sx={styles.select}>
          <Select
            onChange={handleChange as SelectProps['onChange']}
            startIcon={!!ListIcon ? <ListIcon /> : undefined}
            placeholder={placeholder}
            options={options.map((option) => ({
              label: t(option.value),
              value: option.value,
            }))}
            value={value ? { value, label: t(value) } : ''}
          />
        </Box>
        <Box>
          <Box as="span" sx={{ color: 'neutral.200', width: '6px' }}>
            {'|'}
          </Box>
          <IconButton
            data-testid="clear-button"
            variant="text"
            sx={{ height: '38px' }}
            onClick={() => {
              onClear(value);
            }}
          >
            <CloseIcon size="12px" />
          </IconButton>
        </Box>
      </Flex>
      <Box sx={{ paddingBottom: 3, paddingTop: 8, paddingX: 6 }}>
        {value && sel && (
          <FilterSelects
            title={value}
            options={sel.config}
            startVariant={sel.startVariant}
          />
        )}
      </Box>
    </Box>
  );
}

export default FilterSelectsGroup;
